"use client";

import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CircleCheck, CloudUpload } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
export default function ListYourHome() {
  return;
}
function ConciergeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    watch
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const watchedFields = watch();
  const canGoNext = () => {
    if (step === 1) {
      return !!(watchedFields.applicant_name && watchedFields.applicant_email && watchedFields.applicant_phone && watchedFields.address && watchedFields.housing_type && watchedFields.has_yard && watchedFields.yard_fenced);
    }
    if (step === 2) {
      return !!(watchedFields.number_of_current_pets !== "" && watchedFields.dog_experience);
    }
    return true;
  };
  const nextStep = () => {
    if (step < totalSteps && canGoNext()) {
      window.scrollTo({
        top: document.getElementById('puppy-application')?.offsetTop || 0,
        behavior: 'smooth'
      });
      setStep(step + 1);
    }
  };
  const prevStep = () => {
    if (step > 1) {
      window.scrollTo({
        top: document.getElementById('puppy-application')?.offsetTop || 0,
        behavior: 'smooth'
      });
      setStep(step - 1);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-32 text-center space-y-8 animate-in fade-in zoom-in-95 duration-700">
                <div className="size-24 rounded-none bg-primary/10 flex items-center justify-center text-primary mb-4 shadow-md">
                    <CircleCheck className="size-12" />
                </div>
                <h3 className="text-4xl font-sans font-bold text-foreground">Application Received</h3>
                <p className="text-muted-foreground text-xl max-w-md font-light leading-relaxed">
                    {successMessage}
                </p>
            </div>;
  }
  return <div className="w-full flex flex-col bg-card/30 backdrop-blur-sm border border-border/50 p-8 md:p-12 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] rounded-none">
            <div className="mb-16 space-y-6">
                <div className="flex justify-between text-sm font-medium text-muted-foreground tracking-widest uppercase">
                    <span>Step {step} of {totalSteps}</span>
                    <span>
                        {step === 1 ? 'Contact & Housing' : step === 2 ? 'Pets & Experience' : 'Lifestyle & References'}
                    </span>
                </div>
                <Progress value={step / totalSteps * 100} className="h-1 bg-primary/20" />
            </div>

            <Form onSubmit={handleSubmit} className="space-y-8 flex-1 flex flex-col">
                {}
                <div className={cn("space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700", step !== 1 && "hidden")}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <FormField control={control} name="applicant_name" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Applicant Name *</FormLabel>
                                    <FormControl>
                                        <input {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-2xl outline-none transition-colors placeholder:text-muted-foreground/30 font-light rounded-none" placeholder="Jane Smith" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="applicant_email" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Email Address *</FormLabel>
                                    <FormControl>
                                        <input type="email" {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-2xl outline-none transition-colors placeholder:text-muted-foreground/30 font-light rounded-none" placeholder="jane@example.com" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <FormField control={control} name="applicant_phone" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Phone Number *</FormLabel>
                                    <FormControl>
                                        <input type="tel" {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-2xl outline-none transition-colors placeholder:text-muted-foreground/30 font-light rounded-none" placeholder="(303) 555-0123" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="housing_type" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Housing Type *</FormLabel>
                                    <FormControl>
                                        <select {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-2xl outline-none transition-colors cursor-pointer text-foreground font-light appearance-none rounded-none">
                                            <option value="" disabled className="bg-card text-muted-foreground">Select housing type</option>
                                            <option value="House" className="bg-card">House</option>
                                            <option value="Apartment" className="bg-card">Apartment</option>
                                            <option value="Condo" className="bg-card">Condo</option>
                                            <option value="Townhome" className="bg-card">Townhome</option>
                                            <option value="Other" className="bg-card">Other</option>
                                        </select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    <FormField control={control} name="address" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Residential Address *</FormLabel>
                                <FormControl>
                                    <textarea {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors placeholder:text-muted-foreground/30 min-h-[80px] resize-none font-light leading-relaxed rounded-none" placeholder="123 Aspen Way, Denver, CO 80202" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <FormField control={control} name="has_yard" render={({
            field
          }) => <FormItem className="space-y-4">
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Do you have a yard? *</FormLabel>
                                    <FormControl>
                                        <div className="flex gap-8">
                                            {["Yes", "No"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                                                    <input type="radio" {...field} value={option} checked={field.value === option} className="size-5 border-2 border-border/60 text-primary focus:ring-primary/20 cursor-pointer accent-primary" />
                                                    <span className="text-lg font-light group-hover:text-primary transition-colors">{option}</span>
                                                </label>)}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="yard_fenced" render={({
            field
          }) => <FormItem className="space-y-4">
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Is the yard fenced? *</FormLabel>
                                    <FormControl>
                                        <div className="flex gap-8">
                                            {["Yes", "No"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                                                    <input type="radio" {...field} value={option} checked={field.value === option} className="size-5 border-2 border-border/60 text-primary focus:ring-primary/20 cursor-pointer accent-primary" />
                                                    <span className="text-lg font-light group-hover:text-primary transition-colors">{option}</span>
                                                </label>)}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>
                </div>

                {}
                <div className={cn("space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700", step !== 2 && "hidden")}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <FormField control={control} name="number_of_current_pets" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Number of Pets Currently Owned *</FormLabel>
                                    <FormControl>
                                        <input type="number" {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-2xl outline-none transition-colors placeholder:text-muted-foreground/30 font-light rounded-none" placeholder="0" min="0" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="dog_experience" render={({
            field
          }) => <FormItem className="space-y-4">
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Prior Experience with Dogs? *</FormLabel>
                                    <FormControl>
                                        <div className="flex gap-8">
                                            {["Yes", "No"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                                                    <input type="radio" {...field} value={option} checked={field.value === option} className="size-5 border-2 border-border/60 text-primary focus:ring-primary/20 cursor-pointer accent-primary" />
                                                    <span className="text-lg font-light group-hover:text-primary transition-colors">{option}</span>
                                                </label>)}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    <FormField control={control} name="pet_types" render={({
          field
        }) => <FormItem className="space-y-6">
                                <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Types of Pets Currently Owned</FormLabel>
                                <FormControl>
                                    <div className="flex flex-wrap gap-8">
                                        {["Dog", "Cat", "Other"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                                                <input type="checkbox" checked={field.value?.includes(option)} onChange={e => {
                  const current = field.value || [];
                  const next = e.target.checked ? [...current, option] : current.filter((v: string) => v !== option);
                  field.onChange(next);
                }} className="size-5 border-2 border-border/60 rounded-none text-primary focus:ring-primary/20 cursor-pointer accent-primary" />
                                                <span className="text-lg font-light group-hover:text-primary transition-colors">{option}</span>
                                            </label>)}
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="current_pet_details" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Describe Your Current Pets</FormLabel>
                                <FormControl>
                                    <textarea {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors placeholder:text-muted-foreground/30 min-h-[120px] resize-y font-light leading-relaxed rounded-none" placeholder="Breed, age, temperament, any special needs, etc." />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />
                </div>

                {}
                <div className={cn("space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700", step !== 3 && "hidden")}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <FormField control={control} name="lifestyle" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Lifestyle & Daily Routine *</FormLabel>
                                    <FormControl>
                                        <select {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors cursor-pointer text-foreground font-light appearance-none rounded-none">
                                            <option value="" disabled className="bg-card text-muted-foreground">Select your lifestyle</option>
                                            <option value="Very Active – Daily walks and outdoor activities" className="bg-card">Very Active – Daily walks and outdoor activities</option>
                                            <option value="Moderately Active – Walks a few times a week" className="bg-card">Moderately Active – Walks a few times a week</option>
                                            <option value="Mostly Indoor – Limited outdoor time" className="bg-card">Mostly Indoor – Limited outdoor time</option>
                                        </select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="work_schedule" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Work Schedule *</FormLabel>
                                    <FormControl>
                                        <select {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors cursor-pointer text-foreground font-light appearance-none rounded-none">
                                            <option value="" disabled className="bg-card text-muted-foreground">Select your work schedule</option>
                                            <option value="Full‑time (outside the home)" className="bg-card">Full‑time (outside the home)</option>
                                            <option value="Part‑time" className="bg-card">Part‑time</option>
                                            <option value="Work From Home" className="bg-card">Work From Home</option>
                                            <option value="Retired / Not Working" className="bg-card">Retired / Not Working</option>
                                        </select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    <FormField control={control} name="reason_for_cavalier" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Why do you want a Cavalier King Charles Spaniel? *</FormLabel>
                                <FormControl>
                                    <textarea {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors placeholder:text-muted-foreground/30 min-h-[120px] resize-y font-light leading-relaxed rounded-none" placeholder="Tell us why you believe a Cavalier is the right fit for your family." />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="home_photos" render={({
          field: {
            value,
            onChange,
            ...field
          }
        }) => <FormItem>
                                <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Home & Yard Photos (optional)</FormLabel>
                                <FormControl>
                                    <div className="relative border-2 border-dashed border-border/40 hover:border-primary/50 bg-background/30 transition-all duration-300 p-12 text-center cursor-pointer group rounded-none">
                                        <input type="file" multiple accept="image/jpeg, image/png" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => onChange(e.target.files)} {...field} />
                                        <div className="flex flex-col items-center gap-4 pointer-events-none">
                                            <div className="p-5 rounded-none bg-primary/5 text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-all duration-300">
                                                <CloudUpload className="size-8" />
                                            </div>
                                            <span className="text-xl font-light text-foreground/80">Click or drag photos here</span>
                                            <span className="text-sm text-muted-foreground tracking-wide">Upload up to 5 images of the interior and exterior where the puppy will live.</span>
                                        </div>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <FormField control={control} name="vet_reference_name" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Veterinarian Reference – Name</FormLabel>
                                    <FormControl>
                                        <input {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors placeholder:text-muted-foreground/30 font-light rounded-none" placeholder="Dr. Emily Johnson" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="vet_reference_phone" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Veterinarian Reference – Phone</FormLabel>
                                    <FormControl>
                                        <input type="tel" {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors placeholder:text-muted-foreground/30 font-light rounded-none" placeholder="(303) 555-9876" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    <FormField control={control} name="additional_references" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-xl font-sans font-semibold text-foreground/90">Other References (e.g., previous breeders, trainers)</FormLabel>
                                <FormControl>
                                    <textarea {...field} className="w-full bg-transparent border-b border-border/60 focus:border-primary py-4 text-xl outline-none transition-colors placeholder:text-muted-foreground/30 min-h-[100px] resize-y font-light leading-relaxed rounded-none" placeholder="Name, relationship, phone/email" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />
                </div>

                {submitError && <div className="p-6 bg-destructive/10 border border-destructive/20 text-destructive text-base mt-8 rounded-none">
                        {submitError}
                    </div>}

                <div className="pt-16 mt-auto flex items-center justify-between border-t border-border/30">
                    {step > 1 ? <Button type="button" variant="ghost" onClick={prevStep} className="text-muted-foreground hover:text-foreground hover:bg-transparent text-lg px-0 rounded-none h-auto">
                            <ArrowLeft className="mr-3 size-5" /> Back
                        </Button> : <div />}

                    {step < totalSteps ? <Button type="button" onClick={nextStep} disabled={!canGoNext()} className="bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-7 text-lg tracking-wide transition-all duration-300 disabled:opacity-30 rounded-none uppercase font-bold h-auto">
                            Next Step <ArrowRight className="ml-3 size-5" />
                        </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-7 text-lg tracking-wide transition-all duration-300 rounded-none uppercase font-bold h-auto">
                            {isSubmitting ? "Submitting..." : "Submit Application"}
                        </Button>}
                </div>
            </Form>
        </div>;
}